import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, CopyId, ErrorState } from '@bb/ui';
import { ApiError, type AdminApi, type CanvasDraftView, type CanvasMigration, type CanvasSettings, type CanvasValidation, type CanvasValidationIssue, type CanvasView } from '../../../api/client.ts';
import { formatCount } from '../../audience/ChannelReachBreakdown.tsx';
import { duration, type CanvasGraph } from '../editor/CanvasEditor.tsx';
import { MigrationMapDialog } from './MigrationMapDialog.tsx';
import { describeEntry, describeSend, sentence, stepOfIssue, WIZARD_STEPS, type WizardStepId } from './settings.ts';

/** A problem shown in the wizard: where it is fixed, how bad it is and what to do. */
export interface WizardIssue {
  step: WizardStepId;
  severity: 'error' | 'warning';
  message: string;
  stepId?: string | undefined;
}

export const toWizardIssue = (issue: CanvasValidationIssue): WizardIssue => ({ step: stepOfIssue(issue), severity: issue.severity, message: sentence(issue.message), stepId: issue.stepId });

const errorText = (error: unknown) => (error instanceof ApiError || error instanceof Error ? error.message : 'Something went wrong.');
const stepLabel = (step: WizardStepId) => WIZARD_STEPS.find((s) => s.id === step)!.label;

/** Problems grouped by the wizard step that fixes them, each group with a way to that step. */
export function IssueGroups({ issues, stepNames, onGoTo, label }: { issues: WizardIssue[]; stepNames: Record<string, string>; onGoTo: (step: WizardStepId) => void; label: string }) {
  const steps = WIZARD_STEPS.map((s) => s.id).filter((id) => issues.some((i) => i.step === id));
  return (
    <div className="canvas-wizard__issues" aria-label={label} role="group">
      {steps.map((step) => (
        <section key={step} aria-label={`${stepLabel(step)} problems`}>
          <h4>{stepLabel(step)}</h4>
          <ul>
            {issues
              .filter((i) => i.step === step)
              .map((issue, k) => (
                <li key={`${issue.message}-${k}`} className={issue.severity === 'error' ? 'field-error' : 'hint'}>
                  {issue.stepId && stepNames[issue.stepId] ? `«${stepNames[issue.stepId]}»: ` : ''}
                  {issue.message}
                </li>
              ))}
          </ul>
          <Button size="sm" variant="ghost" onClick={() => onGoTo(step)}>
            Go to {stepLabel(step)}
          </Button>
        </section>
      ))}
    </div>
  );
}

export interface SummaryStepProps {
  api: AdminApi;
  environmentId: string;
  canvas: CanvasView;
  /** The draft as loaded: the running version's step names (removed steps). */
  draft: CanvasDraftView;
  graph: CanvasGraph;
  settings: CanvasSettings;
  validation: UseQueryResult<CanvasValidation>;
  /** What the wizard shows: the server's check of the graph and stored settings, the form's own checks of unsaved settings. */
  issues: WizardIssue[];
  dirty: boolean;
  canPublish: boolean;
  /** Saves the graph and the settings; rejects with a message when something could not be saved. */
  onSave: () => Promise<void>;
  onGoTo: (step: WizardStepId) => void;
}

/**
 * Last step of the Canvas wizard (BB-110): every problem by step, the entry, the max duration and what changed against
 * the running version, and Launch / Update. Launch saves pending changes, checks the stored draft and publishes; an
 * update whose new version removes steps that still hold entries asks where they go (migration map) or keeps them on
 * their version.
 */
export function SummaryStep({ api, environmentId, canvas, draft, graph, settings, validation, issues, dirty, canPublish, onSave, onGoTo }: SummaryStepProps) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [launchIssues, setLaunchIssues] = useState<WizardIssue[] | null>(null);
  const [migration, setMigration] = useState<CanvasValidation['requiredMigrations'] | null>(null);
  const live = canvas.publishedVersion !== null;
  const steps = graph.history.present.steps;
  const removedNames = Object.fromEntries(Object.values(draft.published?.definition.steps ?? {}).map((s) => [s.id, s.name]));
  const stepNames = { ...removedNames, ...Object.fromEntries(Object.values(steps).map((s) => [s.id, s.name])) };
  const errors = issues.filter((i) => i.severity === 'error');
  const data = validation.data;

  const publish = async (body: CanvasMigration | undefined) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.publishCanvas(environmentId, canvas.id, body ? { migration: body } : {});
      setMigration(null);
      setLaunchIssues(null);
      const moved = r.migrated ? ` ${formatCount(r.migrated)} running ${r.migrated === 1 ? 'entry' : 'entries'} moved to it.` : '';
      setResult(r.created ? `${live ? `Updated to version ${r.version}.` : `Launched: version ${r.version} is running.`}${moved}` : `Nothing changed since version ${r.version}: no new version was created.`);
      await queryClient.invalidateQueries({ queryKey: ['canvases', environmentId] });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'canvas_invalid') {
        setMigration(null);
        setLaunchIssues(((e.problem.issues as CanvasValidationIssue[] | undefined) ?? []).map(toWizardIssue));
      } else if (e instanceof ApiError && e.code === 'migration_required') {
        // Entries reached removed steps since the check: ask again with the current counts.
        const fresh = await api.validateCanvasDraft(environmentId, canvas.id, graph.history.present).catch(() => null);
        setMigration(fresh?.requiredMigrations.length ? fresh.requiredMigrations : ((e.problem.steps as string[] | undefined) ?? []).map((stepId) => ({ stepId, entries: 0 })));
        setError('Entries have reached more removed steps meanwhile: choose where they go too.');
      } else setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const launch = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    setLaunchIssues(null);
    const definition = graph.history.present;
    try {
      if (dirty) await onSave();
      // What will be published is the stored draft: check it once more for entries on removed steps.
      const check = await api.validateCanvasDraft(environmentId, canvas.id, definition);
      if (!check.valid) return setLaunchIssues(check.issues.filter((i) => i.severity === 'error').map(toWizardIssue));
      if (live && check.requiredMigrations.length) return setMigration(check.requiredMigrations);
    } catch (e) {
      return setError(errorText(e));
    } finally {
      setBusy(false);
    }
    await publish(live ? { mode: 'pin' } : undefined);
  };

  const blocked = canvas.status === 'stopped' ? 'A stopped Canvas cannot be launched again. Duplicate it to start over.' : canvas.status === 'archived' ? 'The Canvas is archived: restore it first.' : null;
  const action = live ? 'Update Canvas' : 'Launch Canvas';

  return (
    <section aria-label="Summary" className="canvas-wizard__panel">
      <h2>Summary</h2>
      <section aria-label="Problems">
        <h3>Problems</h3>
        {validation.isError && !data ? (
          <ErrorState title="The Canvas could not be checked" message={errorText(validation.error)} onRetry={() => void validation.refetch()} />
        ) : !data ? (
          <p aria-busy="true">Checking the Canvas…</p>
        ) : (
          <>
            {errors.length === 0 && <p role="status">{issues.length ? 'No blocking problems; see the warnings below.' : 'No problems: the Canvas is ready to launch.'}</p>}
            <IssueGroups issues={issues} stepNames={stepNames} onGoTo={onGoTo} label="Problems by step" />
          </>
        )}
        {dirty && <p className="notice notice--neutral">You have unsaved changes. {canPublish && !blocked ? `${action} saves them first.` : 'Save them to keep them.'}</p>}
      </section>

      <dl className="facts">
        <dt>Entry</dt>
        <dd>{describeEntry(settings.entry)}</dd>
        {settings.entry?.type === 'api_triggered' && (
          <>
            <dt>Canvas API ID</dt>
            <dd>
              <CopyId label="Canvas API ID" value={canvas.apiId} />
            </dd>
          </>
        )}
        <dt>Entry audience</dt>
        <dd>{settings.audience ? 'Users who match the audience of the Target step' : 'All users'}</dd>
        <dt>Exit criteria</dt>
        <dd>
          {settings.exit.events.length ? `${settings.exit.events.length} exit ${settings.exit.events.length === 1 ? 'event' : 'events'}` : 'No exit events'}
          {settings.exit.audience ? ', an exit audience' : ''}
        </dd>
        <dt>Send settings</dt>
        <dd>{describeSend(settings.send)}</dd>
        <dt>Max duration</dt>
        <dd>{!data ? '…' : data.maxDurationSeconds === null ? 'Unbounded: the graph has a cycle' : duration(data.maxDurationSeconds)}</dd>
      </dl>

      <section aria-label="Changes against the running version">
        <h3>Changes against the running version</h3>
        {!data ? null : !data.diff ? (
          <p className="hint">Not launched yet: there is no running version to compare with.</p>
        ) : data.diff.added.length + data.diff.removed.length + data.diff.changed.length === 0 ? (
          <p className="hint">The steps are the same as in version {canvas.publishedVersion}.</p>
        ) : (
          <ul>
            {(
              [
                ['added', 'Added'],
                ['changed', 'Changed'],
                ['removed', 'Removed'],
              ] as const
            )
              .filter(([key]) => data.diff![key].length)
              .map(([key, text]) => (
                <li key={key}>
                  {text}: {data.diff![key].map((id) => `«${stepNames[id] ?? id}»`).join(', ')}
                </li>
              ))}
          </ul>
        )}
        {data && data.requiredMigrations.length > 0 && (
          <p className="notice notice--warning">
            {formatCount(data.requiredMigrations.reduce((n, r) => n + r.entries, 0))} users are on steps this update removes: you choose where they go before the update.
          </p>
        )}
      </section>

      <section aria-label={live ? 'Update' : 'Launch'} className="canvas-wizard__launch">
        {blocked ? (
          <p className="hint">{blocked}</p>
        ) : !canPublish ? (
          <p className="hint">Launching and updating need the messages.publish permission.</p>
        ) : (
          <>
            {live && <p className="hint">New entries use the new version. Users already in the Canvas finish on their version unless the update has to move them.{canvas.status !== 'active' ? ' The Canvas stays paused after the update.' : ''}</p>}
            <Button variant="primary" loading={busy} disabled={busy || !data || errors.length > 0} onClick={() => void launch()}>
              {action}
            </Button>
            {data && errors.length > 0 && <p className="hint">Fix the problems above to {live ? 'update' : 'launch'}.</p>}
          </>
        )}
        {result && <p role="status">{result}</p>}
        {error && !migration && <p role="alert">{error}</p>}
        {launchIssues && (
          <div role="alert">
            <p>The Canvas is not ready to {live ? 'update' : 'launch'}:</p>
            <IssueGroups issues={launchIssues} stepNames={stepNames} onGoTo={onGoTo} label="Problems found at launch" />
          </div>
        )}
      </section>

      {migration && (
        <MigrationMapDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setMigration(null);
              setError(null);
            }
          }}
          required={migration}
          removedNames={removedNames}
          targets={Object.values(steps).map((s) => ({ id: s.id, name: s.name }))}
          busy={busy}
          error={error}
          onMigrate={(map) => void publish({ mode: 'migrate', map })}
          onPin={() => void publish({ mode: 'pin' })}
        />
      )}
    </section>
  );
}
